import type { ListingPageContent } from "@/features/search/api/listing-page-content";

/**
 * WHO-5542: `{{count}}` in Strapi listing-page copy stands for the page's live result count.
 *
 * The vintage style pages published on 2026-09-09 quoted their inventory in prose and in the
 * head title ("Right now Whoppah lists 871 vintage sofas", "Vintage bank kopen (871 stuks)").
 * Those numbers were true for a day. Editors now write `{{count}}` instead and the loader fills
 * it in from the same total the pagination renders, formatted for the page's locale.
 *
 * When there is no count to show (the search failed, or the page is empty) the copy must not
 * say "0": a parenthetical around the placeholder goes as a whole, a sentence built around it
 * goes as a whole, and a bare placeholder simply disappears.
 */

/** `{{count}}`, tolerating spaces inside the braces. */
const PLACEHOLDER = /\{\{\s*count\s*\}\}/g;

/**
 * A parenthetical holding the placeholder, with the whitespace before it: " ({{count}} stuks)"
 * in "Vintage bank kopen ({{count}} stuks) | Whoppah".
 */
const PARENTHETICAL_WITH_PLACEHOLDER =
  /\s*\([^()]*\{\{\s*count\s*\}\}[^()]*\)/g;

/**
 * A sentence holding the placeholder: from the previous sentence end (or line start) to the next
 * `.`, `!` or `?`. Bounded by the line as well, so a Markdown heading or list item never bleeds
 * into the next one.
 */
const SENTENCE_WITH_PLACEHOLDER =
  /[^.!?\n]*\{\{\s*count\s*\}\}[^.!?\n]*[.!?]*/g;

/** The same sentence when it opens a line, taking the whitespace after it as well. */
const OPENING_SENTENCE_WITH_PLACEHOLDER =
  /^[^.!?\n]*\{\{\s*count\s*\}\}[^.!?\n]*[.!?]*[ \t]*/gm;

export const hasCountPlaceholder = (text: string | null | undefined): boolean =>
  typeof text === "string" && new RegExp(PLACEHOLDER.source).test(text);

/**
 * Locale-grouped integer: 1.130 (nl, de, es, it), 1,130 (en), 1 130 (fr). Grouping is forced:
 * Spanish CLDR data only groups from five digits ("1130" but "11.513"), and the copy should
 * read the same way on every page.
 */
export const formatResultCount = (
  count: number,
  locale: string | undefined
): string =>
  new Intl.NumberFormat(locale ?? "en", { useGrouping: "always" }).format(
    count
  );

const isCountAvailable = (count: number | null | undefined): count is number =>
  typeof count === "number" && Number.isFinite(count) && count > 0;

const collapseSpaces = (text: string) =>
  text.replace(/[ \t]{2,}/g, " ").replace(/[ \t]+$/gm, "");

/**
 * Head-tag copy (`seo.title`, `seo.metaDescription`): the placeholder becomes the count, or,
 * without one, the parenthetical around it is dropped and any remaining placeholder is
 * removed. Text without a placeholder passes through untouched.
 */
export const resolveCountInHeadText = (
  text: string | null,
  count: number | null | undefined,
  locale: string | undefined
): string | null => {
  if (!text || !hasCountPlaceholder(text)) {
    return text;
  }

  if (isCountAvailable(count)) {
    return text.replace(PLACEHOLDER, formatResultCount(count, locale));
  }

  return collapseSpaces(
    text.replace(PARENTHETICAL_WITH_PLACEHOLDER, "").replace(PLACEHOLDER, "")
  ).trim();
};

/**
 * Body copy (`description`, Markdown): the placeholder becomes the count, or, without one, every
 * sentence that leans on it is removed. Text without a placeholder passes through untouched.
 */
export const resolveCountInDescription = (
  text: string | null,
  count: number | null | undefined,
  locale: string | undefined
): string | null => {
  if (!text || !hasCountPlaceholder(text)) {
    return text;
  }

  if (isCountAvailable(count)) {
    return text.replace(PLACEHOLDER, formatResultCount(count, locale));
  }

  /*
   * A sentence that opens a line has no leading space to give up, so the space after it goes
   * instead; mid-paragraph the match already starts with the space after the previous full stop,
   * and the space before the next sentence stays behind, so the join reads "one. Two."
   */
  const withoutOpeningSentences = text.replace(
    OPENING_SENTENCE_WITH_PLACEHOLDER,
    ""
  );

  return collapseSpaces(
    withoutOpeningSentences.replace(SENTENCE_WITH_PLACEHOLDER, "")
  );
};

/** The page's copy with every `{{count}}` resolved against the live total. */
export const resolveListingPageContentCounts = (
  content: ListingPageContent | null,
  count: number | null | undefined,
  locale: string | undefined
): ListingPageContent | null => {
  if (!content) {
    return content;
  }

  return {
    ...content,
    description: resolveCountInDescription(content.description, count, locale),
    seoTitle: resolveCountInHeadText(content.seoTitle, count, locale),
    seoDescription: resolveCountInHeadText(
      content.seoDescription,
      count,
      locale
    ),
  };
};
